import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import Text from '../Text';
import { Button } from '../Button';
import { RootState } from 'store/ducks';
import Select from 'components/Select';
import { Row, Column } from '../Layout/Grid';
import { MultiSelectCustomized } from 'components/MultiSelect/MultiSelectCustomized';
import { ICategoriesReasons, IPipefyOption } from 'components/Select/interface';

import arrowFilter from 'img/arrow-filter.svg';
import IconSearch from 'img/search-icon.svg';

import { ITableSearchProtocol } from './interface';

import DomainSettingService from 'services/domainSetting.service';
import PipefyServices from 'services/pipefy.service';
import BusService from 'services/bus.service';
import BrandService from 'services/brand.service';
import SubsidiaryService from 'services/subsidiary.service';
import TicketServices from 'services/ticket.service';

import { BUSelect, BUType, IBusinessUnits } from 'models/BusinessUnits';
import { IDomainSetting } from 'models/DomainSetting';
import { ISubsidiary } from 'models/Subsidiary';
import { IRequestFilter, TicketTabsEnum } from 'models/Ticket';
import { IBrand } from 'models/brand';

import { useTableSearchProtocolContext } from 'contexts/TableSearchProtocol';
import { addKeyPressListener, removeKeyPressListener } from 'utils/searchWithEnterPress';
import { calcDataFrom, calcDataTo } from 'utils/Date';

import './style.scss';
import PeriodDateToInput from 'components/PeriodDateToInput';

const TableSearchProtocol: React.FC<ITableSearchProtocol> = (props) => {
  const {
    fastFilters,
    statusFilters,
    dateKey,
    tableOrderBy,
    setTableOrderBy,
    className,
    setCurrentPage,
    showGroupSelect,
    ShowStatusSelect,
    clearBtn,
    statusLabel,
    typeStatusProtocol,
    showBUs,
    showAdvancedFilters,
    showCategories,
    showReasons,
    showSubsidiaries,
    showBrands,
    setFilterProtocols,
    statusTypes,
    setOnLoadPageTable
  } = props;

  const {
    addNumberProtocol,
    searchNumberProtocol,
    setNewData,
    newData,
    period,
    setPeriod,
    addCategorySelected,
    categorySelected,
    addReasonsSelected,
    reasonsSelected,
    addDescription,
    searchStr,
    addResponsibleGroup,
    profilesFilter,
    addBrands,
    brandsSelected,
    addBUs,
    busFilter,
    addSubsidiaries,
    subsidiariesSelected,
    addStatusProtocol,
    selectedPhase,
    showAdvancedFilter,
    advancedFilters
  } = useTableSearchProtocolContext();

  const currentTabId = useSelector((state: RootState) => state.currentTab?.currentNameTab);
  const user = useSelector((state: RootState) => state.user);

  const randomTableSearchId = `${Math.random() * 1000}`;

  const _domainSettingService = new DomainSettingService();
  const _busService = new BusService();
  const _brandsService = new BrandService();
  const _subsidiaryService = new SubsidiaryService();
  const _protocolService = new TicketServices();
  const _pipefyService = new PipefyServices();

  const [selectedOption, setSelectedOption] = useState(JSON.stringify({}));
  const [phases, setPhases] = useState<any[]>([]);
  const [showPhasesDropdown, setShowPhasesDropdown] = useState<boolean>(false);

  const [userProfileTypes, setUserProfileTypes] = useState(Array<IDomainSetting>());
  const [allBUs, setAllBUs] = useState<Array<BUSelect>>([]);
  const [allBrands, setAllBrands] = useState<Array<BUSelect>>([]);
  const [allSubsidiaries, setAllSubsidiaries] = useState<Array<BUSelect>>([]);
  const [allCategories, setAllCategories] = useState<Array<IPipefyOption>>([]);
  const [allReasons, setAllReasons] = useState<Array<IPipefyOption>>([]);
  const [showResponsibleGroupDropdown, setShowResponsibleGroupDropdown] = useState<boolean>(false);
  const [showBUsDropdown, setShowBUsDropdown] = useState<boolean>(false);
  const [showBrandsDropdown, setShowBrandsDropdown] = useState<boolean>(false);
  const [showSubsidiaryDropdown, setShowSubsidiaryDropdown] = useState<boolean>(false);

  const searchStrRef = useRef(searchStr);
  const searchNumberProtocolRef = useRef(searchNumberProtocol);
  const categorySelectedRef = useRef(categorySelected);
  const allCategoriesRef = useRef(allCategories);
  const reasonsSelectedRef = useRef(reasonsSelected);
  const allReasonsRef = useRef(allReasons);
  const periodRef = useRef(period);
  const selectedOptionRef = useRef(selectedOption);
  const busFilterRef = useRef(busFilter);
  const profilesFilterRef = useRef(profilesFilter);
  const subsidiariesSelectedRef = useRef(subsidiariesSelected);
  const selectedPhaseRef = useRef(selectedPhase);
  const brandsSelectedRef = useRef(brandsSelected);

  useEffect(() => {
    if (currentTabId !== TicketTabsEnum.PROTOCOLO_ABERTO)
      addStatusProtocol([]);
  }, [currentTabId]);

  useEffect(() => {
    getCategoriesReasons();
  }, []);

  const getCategoriesReasons = async () => {
    try {
      const options: ICategoriesReasons = await _protocolService.getCategoriesReasons();
      const { data } = options?.data ?? {};
      setAllCategories(data?.categories ?? []);
      setAllReasons(data?.reasons ?? []);
    } catch {
      setAllCategories([]);
      setAllReasons([]);
    }
  };

  useEffect(() => {
    getPhases();
  }, []);

  useEffect(() => {
    if (!(user?.username || user?.userName || user?.id)) return;

    makeFilterLoadPage(
      user?.profileType || [],
      user?.businessUnits,
      user.brands,
      user.subsidiaries
    );
  }, [user, allCategories, allReasons]);

  useEffect(() => {

    if (busFilter.length === 0) {
      const buSelected: Array<string> = [];
      user?.businessUnits?.map(bu => {
        if (bu.id) {
          buSelected.push(bu.id);
        }
      });

      addBUs(buSelected);
    }

    if (brandsSelected?.length === 0) {
      const brandsSelectedConst: Array<string> = [];
      user?.brands?.map(brand => {
        if (brand.id) {
          brandsSelectedConst.push(brand.id);
        }
      });

      addBrands(brandsSelectedConst);
    }

    if (subsidiariesSelected?.length === 0) {
      const subsidiariesSelectedConst: Array<string> = [];
      user?.subsidiaries?.map(branch => {
        if (branch.id) {
          subsidiariesSelectedConst.push(branch.id);
        }
      });

      addSubsidiaries(subsidiariesSelectedConst);
    }

    const userProfileLogged: any = user?.profileType;

    const indexCustomerLogged = userProfileLogged?.indexOf("CUSTOMER");

    if (indexCustomerLogged !== -1) {
      userProfileLogged?.splice(indexCustomerLogged, 1);
    }

    if (profilesFilter?.length === 0) {
      const newProfiles = user?.profileType ? [...user?.profileType] : [];
      addResponsibleGroup(newProfiles);
    }

  }, [user]);

  function makeFilter() {
    const searchStrFilter = searchStrRef.current;
    const searchNumberProtocolFilter = searchNumberProtocolRef.current;
    const periodFilter = periodRef.current;
    const categorySelectedFilter = categorySelectedRef.current;
    const allCategoriesFilter = allCategoriesRef.current;
    const reasonsSelectedFilter = reasonsSelectedRef.current;
    const allReasonsFilter = allReasonsRef.current;
    const busFilterNew = busFilterRef.current;
    const profilesFilterNew = profilesFilterRef.current;
    const subsidiariesSelectedFilter = subsidiariesSelectedRef.current;
    const brandsSelectedFilter = brandsSelectedRef.current;

    const requestFilter: IRequestFilter = {
      protocolNumber: searchNumberProtocolFilter ? Number(searchNumberProtocolFilter) : '',
      startPeriod: periodFilter.from,
      lastPeriod: periodFilter.to,
      keyword: searchStrFilter,
      bus: busFilterNew,
      responsibleGroups: profilesFilterNew,
      categories: categorySelectedFilter ? [allCategoriesFilter.find(x => x.value === categorySelectedFilter)?.pipefyLabel] : [],
      reasons: reasonsSelectedFilter ? [allReasonsFilter.find(x => x.value === reasonsSelectedFilter)?.pipefyLabel] : [],
      subsidiaryIds: subsidiariesSelectedFilter,
      phaseStatus: setNewStatusProtocols(),
      brands: brandsSelectedFilter,
      statusType: statusTypes,
    };

    setOnLoadPageTable && setOnLoadPageTable(true);
    setFilterProtocols && setFilterProtocols(requestFilter);
    setCurrentPage(0);
  }

  function setNewStatusProtocols() {
    const newSelectedPhases = [...selectedPhaseRef.current];
    if (newSelectedPhases.includes('Avaliação comercial')) {
      newSelectedPhases.push('Avaliação Comercial Interno');
    }

    return newSelectedPhases;
  }

  function makeFilterLoadPage(
    filterGroup: Array<string>,
    filterBU?: Array<IBusinessUnits>,
    filterBrand?: Array<IBrand>,
    filterSubsidiary?: Array<ISubsidiary>
  ) {

    const bus: Array<string> = [];
    filterBU?.map(bu => {
      if (bu.id) bus.push(bu.id);
    });

    const subsidiaries: Array<string> = [];
    filterSubsidiary?.map(subsidiary => {
      if (subsidiary.id) subsidiaries.push(subsidiary.id);
    });

    const brands: Array<string> = [];
    filterBrand?.map(brand => {
      if (brand.id) brands.push(brand.id);
    });

    const requestFilter: IRequestFilter = {
      protocolNumber: searchNumberProtocol ? Number(searchNumberProtocol) : '',
      startPeriod: period.from,
      lastPeriod: period.to,
      keyword: searchStr,
      categories: categorySelected ? [allCategories.find(x => x.value === categorySelected)?.label] : [],
      reasons: reasonsSelected ? [allReasons.find(x => x.value === reasonsSelected)?.label] : [],
      responsibleGroups: profilesFilter?.length > 0 ? profilesFilter : filterGroup,
      brands: brandsSelected?.length > 0 ? brandsSelected : brands,
      bus: busFilter?.length > 0 ? busFilter : bus,
      subsidiaryIds: subsidiariesSelected?.length > 0 ? subsidiariesSelected : subsidiaries,
      phaseStatus: selectedPhase,
      statusType: statusTypes,
    };

    setOnLoadPageTable && setOnLoadPageTable(true);
    setFilterProtocols && setFilterProtocols(requestFilter);
    setCurrentPage(0);
  }

  function setFastFilter(value: any) {
    if (tableOrderBy === value) return;

    setTableOrderBy(value);
  }

  const fastSearchStyles = (filter: any) =>
    `${tableOrderBy === filter.value && 'border-bottom-2'} font-bold ${tableOrderBy === filter.value ? 'color-primary' : 'color-dark-gray'
    }`;

  useEffect(() => {
    searchStrRef.current = searchStr;
  }, [searchStr]);

  useEffect(() => {
    searchNumberProtocolRef.current = searchNumberProtocol;
  }, [searchNumberProtocol]);

  useEffect(() => {
    periodRef.current = period;
  }, [period]);

  useEffect(() => {
    selectedOptionRef.current = selectedOption;
  }, [selectedOption]);

  useEffect(() => {
    categorySelectedRef.current = categorySelected;
  }, [categorySelected]);

  useEffect(() => {
    allCategoriesRef.current = allCategories;
  }, [allCategories]);

  useEffect(() => {
    reasonsSelectedRef.current = reasonsSelected;
  }, [reasonsSelected]);

  useEffect(() => {
    allReasonsRef.current = allReasons;
  }, [allReasons]);

  useEffect(() => {
    busFilterRef.current = busFilter;
  }, [busFilter]);

  useEffect(() => {
    profilesFilterRef.current = profilesFilter;
  }, [profilesFilter]);

  useEffect(() => {
    subsidiariesSelectedRef.current = subsidiariesSelected;
  }, [subsidiariesSelected]);

  useEffect(() => {
    selectedPhaseRef.current = selectedPhase;
  }, [selectedPhase]);

  useEffect(() => {
    brandsSelectedRef.current = brandsSelected;
  }, [brandsSelected]);

  useEffect(() => {
    addKeyPressListener(randomTableSearchId, makeFilter);

    return () => {
      removeKeyPressListener(randomTableSearchId, makeFilter);
    };
  }, []);

  useEffect(() => {
    _busService
      .get(undefined, undefined, 'name asc', {})
      .then((result: any) => {
        if (result && result.data) {
          const bus: Array<BUType> = result.data;
          let allBusSelect: Array<BUSelect> = [];
          bus.forEach((bu: BUType) => {
            const item = {
              value: `${bu.identifier} - ${bu.name}`,
              key: bu.id,
            };

            allBusSelect.push({ ...allBUs, ...item });
          });
          setAllBUs(allBusSelect);
        }
      });
  }, []);

  useEffect(() => {
    _brandsService
      .get(undefined, undefined, 'name asc', {})
      .then((result: any) => {
        if (result && result.data) {
          const brands: Array<BUType> = result.data;
          let allBrandsNew: Array<BUSelect> = [];
          brands.forEach((brand: BUType) => {
            const item = {
              value: brand.name,
              key: brand.id,
            };

            allBrandsNew.push({ ...allBrandsNew, ...item });
          });
          setAllBrands(allBrandsNew);
        }
      });
  }, []);

  useEffect(() => {
    _subsidiaryService
      .get(undefined, undefined, 'enterpriseName asc', {})
      .then((result: any) => {
        if (result && result.data) {
          const subsidiaries: Array<ISubsidiary> = result.data;
          let allSubsidiariesNew: Array<BUSelect> = [];
          subsidiaries.forEach((subsidiary: ISubsidiary) => {
            if (subsidiary.fullName && subsidiary.id) {
              const item = {
                value: subsidiary.fullName,
                key: subsidiary.id,
              };

              allSubsidiariesNew.push({ ...allSubsidiariesNew, ...item });
            }
          });
          setAllSubsidiaries(allSubsidiariesNew);
        }
      });
  }, []);

  useEffect(() => {
    _domainSettingService
      .get(undefined, undefined, 'value asc', {
        and: [
          { identifier: { eq: 'UserProfileTypes' } },
          { enabled: { eq: true } },
        ],
      })
      .then((result: any) => {
        if (result && result.data) {
          const _userProfileTypes: Array<IDomainSetting> = result.data;

          const indexCustomer = _userProfileTypes.findIndex(item => item.key === "CUSTOMER");

          if (indexCustomer !== -1) {
            _userProfileTypes.splice(indexCustomer, 1);
          }

          setUserProfileTypes(_userProfileTypes);
        }
      });
  }, []);

  async function getPhases() {
    const response = await _pipefyService.getPhases();

    let result;

    if (typeStatusProtocol === 'Abertos') {
      result = response.filter(p => p.done === false && p.name !== 'Avaliação Comercial Interno');
    } else {
      result = response.filter(p => p.done === true);
    }

    setPhases(result);
  }

  async function AdvancedFiltersLoad() {
    if (phases.length <= 1) {
      await getPhases();
    }

    showAdvancedFilter(!advancedFilters);
  }

  const clearFilter = () => {
    addNumberProtocol("");
    addCategorySelected("");
    addReasonsSelected("");
    addDescription("");
    addResponsibleGroup([]);
    addBrands([]);
    addBUs([]);
    addSubsidiaries([]);
    addStatusProtocol([]);

    setCurrentPage(0);

    const requestFilter: IRequestFilter = {
      protocolNumber: '',
      startPeriod: period.from,
      lastPeriod: period.to,
      keyword: '',
      bus: [],
      responsibleGroups: [],
      categories: [],
      reasons: [],
      subsidiaryIds: [],
      phaseStatus: [],
      brands: [],
      statusType: statusTypes
    };

    setFilterProtocols && setFilterProtocols(requestFilter);
  };

  return (
    <Column
      id={randomTableSearchId}
      tabIndex={0}>
      <Row
        className={`mt-4 align-center flex-md-column align-md-none flex-wrap justify-md-none bg-white ${className || 'px-3 py-3'
          }`}
        style={{ borderRadius: '10px 10px 0px 0px' }}
      >

        <Column className="mr-2 mr-md-2 w-md-100 ajustment-filter">
          <Text className="mb-1 color-blue-gray" size={12} weight="bold">
            Filtro
          </Text>

          <Row>
            <input
              onChange={(e) => addNumberProtocol(e.target.value)}
              type="text"
              placeholder='Nº do protocolo'
              value={searchNumberProtocol}
              className="w-sm-100 w-md-100 w-number-protocol"
            />
          </Row>

        </Column>
        {dateKey && (

          <Column className="flex-1 mr-2 mr-md-2 w-md-100 mt-md-3">
            <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
              Período
            </Text>
            <Row className="flex-1 align-center">
              <input
                className="flex-1"
                value={period.from}
                onChange={(e) => {
                  setPeriod({ ...period, from: e.target.value });
                  setNewData('end');
                }}
                type="date"
                max={period.to}
                placeholder="Início"
                onBlur={() => calcDataTo(period, newData, setPeriod)}
              />
              <Text className="mx-1 color-blue-gray" size={12} weight="bold">
                a
              </Text>
              <PeriodDateToInput
                setNewData={setNewData}
                onBlur={() => calcDataFrom(period, newData, setPeriod)}
                className="flex-1"
                period={period}
                setPeriod={setPeriod}
                placeHolder={"Fim"}
              />
            </Row>
          </Column>
        )}

        {statusFilters && (
          <Column className="mr-2 mr-lg-0 mt-md-3 flex-1 w-md-100 ajustment-status">
            <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
              Status
            </Text>
            <Row className="flex-1">
              <select
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="flex-1"
              >
                <option value={JSON.stringify({})}>Todos</option>
                {statusFilters.map((filter, index) => (
                  <option
                    key={index}
                    label={filter.label}
                    value={JSON.stringify(filter.value)}
                  />
                ))}
              </select>
            </Row>
          </Column>
        )}

        {/* NOVOS FILTROS VÃO AQUI */}
        {showCategories && (
          <Column className="mr-2 mr-md-2 category-reason">
            <Text size={12} className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2">
              Categoria
            </Text>
            <Row className="align-center flex-sm-column">
              <Select
                stateName="protocolCategory"
                onChange={(e) => addCategorySelected(e.target.value)}
                name="Categoria do protocolo"
                id="PROTOCOL_CATEGORY"
                value={categorySelected}
                options={allCategories}
                width="140px"
                isFilterProtocols
              />
            </Row>
          </Column>
        )}

        {showReasons && (
          <Column className="mr-2 mr-md-2 category-reason">
            <Text className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2" size={12}>
              Motivo
            </Text>
            <Row className="align-center flex-sm-column">
              <Select
                stateName="ticketReason"
                onChange={(e) => addReasonsSelected(e.target.value)}
                name="Motivo do protocolo"
                subjection={categorySelected}
                id="PROTOCOL_REASON"
                value={reasonsSelected}
                options={allReasons}
                width="140px"
                isFilterProtocols
              />
            </Row>
          </Column>
        )}

        <Column className="mr-2 mt-md-3 w-md-100 pt-3 pt-md-0 ajustment-button">
          <Row>
            <Button
              tabIndex={0}
              className="focus-button"
              width="auto"
              onClick={makeFilter}
            >
              <img src={IconSearch} alt="" />
            </Button>
          </Row>
        </Column>
        {fastFilters && (
          <Row className="mt-3 mt-md-3 align-center justify-between flex-1 w-md-100 flex-md-column">
            <Row className="align-center flex-1 justify-start justify-md-center w-md-100 align-md-none">
              {fastFilters.map((filter, index) => (
                <Button
                  key={index}
                  className={`${index > 0 && 'ml-0 ml-md-0'}`}
                  onClick={() => setFastFilter(filter.value)}
                  width="auto"
                >
                  <Text
                    size={12}
                    className={`border-primary ${fastSearchStyles(
                      filter
                    )} pb-1 px-2 ajustment-fontsize-fast-filter`}
                  >
                    {filter.label}
                  </Text>
                </Button>
              ))}
            </Row>
          </Row>
        )}
        {showAdvancedFilters && (
          <Row className="justify-end justify-lg-end justify-md-center mt-2 mr-3 arrow-filter-advanced">
            <Button onClick={async () => await AdvancedFiltersLoad()}>
              <Text size={14} className="color-primary mt-2 ajustment-fontsize-clear-filter">
                <img
                  src={arrowFilter}
                  alt=""
                  style={{ width: 8, height: 9 }}
                  className={`${advancedFilters && 'inverse-arrow'} mr-1`}
                /> Filtro avançado
              </Text>
            </Button>
          </Row>
        )}
        {clearBtn && (
          <Row className="justify-end justify-lg-end justify-md-center mt-2">
            <Button onClick={() => clearFilter()}>
              <Text size={14} className="color-primary mt-2 ajustment-fontsize-clear-filter">
                x Limpar filtros
              </Text>
            </Button>
          </Row>
        )}
      </Row>

      {/* Filtros avançados */}

      {advancedFilters && (
        <Row
          className={`bg-white`}
        >
          <Row
            className="align-center flex-md-column align-md-none flex-wrap justify-md-none px-3 py-3 mx-3 mb-3 advanced-filters-container"
          >
            <Column className="mr-2 mr-md-2 w-md-100 ajustment-filter">
              <Text className="mb-1 color-blue-gray" size={12} weight="bold">
                Descrição
              </Text>

              <Row>
                <input
                  onChange={(e) => addDescription(e.target.value)}
                  type="text"
                  placeholder={'Palavra-chave'}
                  value={searchStr}
                  className="w-sm-100 w-md-100 w-search"
                />
              </Row>

            </Column>

            {showGroupSelect && (
              <Column className="mr-2 mr-md-2">
                <Text size={12} className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2">
                  Grupo Responsável
                </Text>
                <Row className="align-center flex-sm-column">
                  <MultiSelectCustomized
                    itemsSelected={profilesFilter}
                    allItems={userProfileTypes}
                    width={"330px"}
                    className="w-sm-100 w-md-100"
                    placeHolderOne="grupo"
                    placeHolderMany="grupos"
                    style={{ minWidth: 190 }}
                    show={showResponsibleGroupDropdown}
                    setShow={setShowResponsibleGroupDropdown}
                  />
                </Row>
              </Column>
            )}

            {showBrands && (
              <Column className="mr-2 mr-md-2">
                <Text size={12} className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2">
                  Marca
                </Text>
                <Row className="align-center flex-sm-column">
                  <MultiSelectCustomized
                    itemsSelected={brandsSelected}
                    allItems={allBrands}
                    width={"330px"}
                    className="w-sm-100 w-md-100"
                    placeHolderOne="marca"
                    placeHolderMany="marcas"
                    style={{ minWidth: 190 }}
                    show={showBrandsDropdown}
                    setShow={setShowBrandsDropdown}
                  />
                </Row>
              </Column>
            )}

            {showBUs && (
              <Column className="mr-2 mr-md-2">
                <Text size={12} className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2">
                  BU's
                </Text>
                <Row className="align-center flex-sm-column">
                  <MultiSelectCustomized
                    itemsSelected={busFilter}
                    allItems={allBUs}
                    width={"330px"}
                    className="w-sm-100 w-md-100"
                    placeHolderOne="BU"
                    placeHolderMany="BUs"
                    style={{ minWidth: 190 }}
                    show={showBUsDropdown}
                    setShow={setShowBUsDropdown}
                  />
                </Row>
              </Column>
            )}

            {showSubsidiaries && (
              <Column className="mr-2 mr-md-2">
                <Text size={12} className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2">
                  Filial
                </Text>
                <Row className="align-center flex-sm-column">
                  <MultiSelectCustomized
                    itemsSelected={subsidiariesSelected}
                    allItems={allSubsidiaries}
                    width={"330px"}
                    className="w-sm-100 w-md-100"
                    placeHolderOne="filial"
                    placeHolderMany="filiais"
                    style={{ minWidth: 190 }}
                    show={showSubsidiaryDropdown}
                    setShow={setShowSubsidiaryDropdown}
                  />
                </Row>
              </Column>
            )}

            {ShowStatusSelect && (
              <Column className="mr-2 mr-md-2">
                <Text size={12} className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2">
                  {statusLabel}
                </Text>
                <Row className="align-center flex-sm-column w-sm-100 overflow-hidden">
                  <MultiSelectCustomized
                    itemsSelected={selectedPhase}
                    allItems={phases}
                    width={"330px"}
                    className="w-sm-100 w-md-100 status"
                    placeHolderOne="Status"
                    placeHolderMany="Status"
                    style={{ minWidth: 140 }}
                    show={showPhasesDropdown}
                    setShow={setShowPhasesDropdown}
                  />
                </Row>
              </Column>
            )}
          </Row>
        </Row>
      )}
    </Column>
  );
};

export default TableSearchProtocol;
